import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { replaceVariables } from '../components/VariablesHelper';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for managing CRM communications (email, SMS, calls)
 */
export function useCrmCommunications(leadId, lead, templates, refetch) {
  // Email form state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ 
    to: '', 
    cc: '', 
    bcc: '',
    subject: '', 
    body: '',
    template_id: '',
    attachments: []
  });
  
  // SMS form state
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsForm, setSmsForm] = useState({ 
    to: '', 
    message: '',
    template_id: ''
  });
  
  // Call form state
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [callForm, setCallForm] = useState({ 
    phone_number: '', 
    notes: '', 
    call_outcome: 'connected', 
    duration_seconds: 0,
    schedule_followup: false,
    followup_date: '',
    followup_time: '09:00',
    mark_dnc: false,
    send_email_after: false,
    email_template_id: '',
    recording_id: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [attachLibraryOpen, setAttachLibraryOpen] = useState(false);
  
  // Refs for cursor position
  const emailBodyRef = useRef(null);
  const smsMessageRef = useRef(null);
  const emailSubjectRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize forms with lead data
  const initForms = (leadData) => {
    if (leadData?.work_email) setEmailForm(f => ({ ...f, to: leadData.work_email }));
    if (leadData?.mobile_phone) setSmsForm(f => ({ ...f, to: leadData.mobile_phone }));
    if (leadData?.work_direct_phone) setCallForm(f => ({ ...f, phone_number: leadData.work_direct_phone }));
  };

  // Apply template to email
  const handleEmailTemplateChange = (templateId) => {
    setEmailForm(f => ({ ...f, template_id: templateId }));
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        const subject = replaceVariables(template.subject || '', lead);
        const body = replaceVariables(template.content || '', lead);
        setEmailForm(f => ({ ...f, subject, body }));
      }
    }
  };

  // Apply template to SMS
  const handleSmsTemplateChange = (templateId) => {
    setSmsForm(f => ({ ...f, template_id: templateId }));
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        const message = replaceVariables(template.content || '', lead);
        setSmsForm(f => ({ ...f, message }));
      }
    }
  };

  // Insert variable at cursor
  const insertVariable = (variable, formType, field) => {
    if (formType === 'email') {
      if (field === 'subject') {
        setEmailForm(f => ({ ...f, subject: f.subject + variable }));
      } else {
        setEmailForm(f => ({ ...f, body: f.body + variable }));
      }
    } else if (formType === 'sms') {
      setSmsForm(f => ({ ...f, message: f.message + variable }));
    }
  };

  // Handle file attachment
  const handleFileAttachment = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: `local-${Date.now()}-${file.name}`,
      name: file.name,
      size: file.size,
      file_size_formatted: `${(file.size / 1024).toFixed(1)} KB`,
      file_type: file.name.split('.').pop()?.toUpperCase() || 'FILE',
      source: 'local',
      file: file
    }));
    setEmailForm(f => ({ ...f, attachments: [...f.attachments, ...newAttachments] }));
  };

  const removeAttachment = (index) => {
    setEmailForm(f => ({
      ...f,
      attachments: f.attachments.filter((_, i) => i !== index)
    }));
  };

  // Add library attachments
  const addLibraryAttachments = (docs) => {
    const newAttachments = docs.map(d => ({
      ...d,
      source: 'library'
    }));
    setEmailForm(f => ({ ...f, attachments: [...f.attachments, ...newAttachments] }));
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.body) { 
      toast.error('Fill all required fields'); 
      return; 
    }
    setSaving(true);
    try {
      const finalSubject = replaceVariables(emailForm.subject, lead);
      const finalBody = replaceVariables(emailForm.body, lead);
      
      // Prepare attachments for API
      const processedAttachments = [];
      for (const att of emailForm.attachments) {
        if (att.source === 'library') {
          processedAttachments.push({
            document_id: att.id,
            filename: att.name
          });
        } else if (att.file) {
          const base64Content = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(att.file);
          });
          processedAttachments.push({
            filename: att.name,
            content: base64Content,
            mime_type: att.file.type || 'application/octet-stream'
          });
        }
      }
      
      await axios.post(`${API}/communications/email`, { 
        lead_id: leadId, 
        to: emailForm.to,
        cc: emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: emailForm.bcc ? emailForm.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: finalSubject,
        body: finalBody,
        attachments: processedAttachments
      });
      toast.success(processedAttachments.length > 0 ? `Email sent with ${processedAttachments.length} attachment(s)` : 'Email sent');
      setEmailDialogOpen(false);
      setEmailForm({ to: lead?.work_email || '', cc: '', bcc: '', subject: '', body: '', template_id: '', attachments: [] });
      refetch();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to send email'); 
    } finally { 
      setSaving(false); 
    }
  };

  // Send SMS
  const handleSendSMS = async () => {
    if (!smsForm.to || !smsForm.message) { 
      toast.error('Fill all fields'); 
      return; 
    }
    setSaving(true);
    try {
      const finalMessage = replaceVariables(smsForm.message, lead);
      
      try {
        await axios.post(`${API}/telnyx/sms`, { 
          to_number: smsForm.to, 
          message: finalMessage,
          lead_id: leadId
        });
        toast.success('SMS sent successfully');
      } catch (telnyxError) {
        if (telnyxError.response?.status === 500 && telnyxError.response?.data?.detail?.includes('not configured')) {
          await axios.post(`${API}/communications/sms`, { lead_id: leadId, to: smsForm.to, message: finalMessage });
          toast.success('SMS logged (Telnyx not configured)');
        } else {
          throw telnyxError;
        }
      }
      
      setSmsDialogOpen(false);
      setSmsForm({ to: lead?.mobile_phone || '', message: '', template_id: '' });
      refetch();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to send SMS'); 
    } finally { 
      setSaving(false); 
    }
  };

  // Log call
  const handleLogCall = async () => {
    if (!callForm.phone_number) { 
      toast.error('Phone number is required'); 
      return; 
    }
    setSaving(true);
    try {
      await axios.post(`${API}/communications/call`, { 
        lead_id: leadId, 
        phone_number: callForm.phone_number,
        notes: callForm.notes,
        call_outcome: callForm.call_outcome,
        duration_seconds: callForm.duration_seconds,
        recording_id: callForm.recording_id || undefined
      });
      
      // Create follow-up task if requested
      if (callForm.schedule_followup) {
        let dueDate;
        if (callForm.followup_date) {
          dueDate = new Date(callForm.followup_date + 'T' + (callForm.followup_time || '09:00'));
        } else {
          dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 1);
          dueDate.setHours(10, 0, 0, 0);
        }
        
        await axios.post(`${API}/tasks`, { 
          lead_id: leadId, 
          description: `Follow-up call with ${lead?.first_name || ''} ${lead?.last_name || ''}`.trim(),
          due_date: dueDate.toISOString()
        });
      }
      
      // Mark DNC if requested
      if (callForm.mark_dnc) {
        await axios.put(`${API}/leads/${leadId}`, { dnc: true });
        toast.success('Call logged & lead marked as Do Not Call');
      } else if (callForm.schedule_followup) {
        toast.success('Call logged & follow-up task created');
      } else {
        toast.success('Call logged');
      }
      
      setCallDialogOpen(false);
      resetCallForm();
      refetch();
    } catch (error) { 
      toast.error('Failed to log call'); 
    } finally { 
      setSaving(false); 
    }
  };

  // Reset call form
  const resetCallForm = () => {
    setCallForm({ 
      phone_number: lead?.work_direct_phone || lead?.mobile_phone || '', 
      notes: '', 
      call_outcome: 'connected', 
      duration_seconds: 0,
      schedule_followup: false,
      followup_date: '',
      followup_time: '09:00',
      mark_dnc: false,
      send_email_after: false,
      email_template_id: '',
      recording_id: ''
    });
  };

  // Send quick email from call dialog
  const handleSendQuickEmail = async () => {
    if (!callForm.email_template_id) { 
      toast.error('Please select an email template'); 
      return; 
    }
    if (!lead?.work_email) {
      toast.error('Lead has no email address');
      return;
    }
    
    const selectedTemplate = templates.find(t => t.id === callForm.email_template_id);
    if (!selectedTemplate) {
      toast.error('Template not found');
      return;
    }
    
    setSaving(true);
    try {
      const finalSubject = replaceVariables(selectedTemplate.subject || '', lead);
      const finalBody = replaceVariables(selectedTemplate.body || '', lead);
      
      await axios.post(`${API}/communications/email`, { 
        lead_id: leadId, 
        to: lead.work_email,
        cc: [],
        bcc: [],
        subject: finalSubject,
        body: finalBody
      });
      toast.success('Email sent successfully');
      setCallForm(f => ({ ...f, email_template_id: '', send_email_after: false }));
    } catch { 
      toast.error('Failed to send email'); 
    } finally { 
      setSaving(false); 
    }
  };

  // Delete communication
  const handleDeleteCommunication = async (commId) => {
    try {
      await axios.delete(`${API}/communications/${commId}`);
      toast.success('Communication deleted');
      refetch();
    } catch { 
      toast.error('Failed to delete'); 
    }
  };

  // Open log call dialog
  const openLogCallDialog = () => {
    const phoneNumber = lead?.work_direct_phone || lead?.mobile_phone || lead?.work_company_phone || '';
    setCallForm(f => ({
      ...f,
      phone_number: phoneNumber,
      notes: '',
      call_outcome: 'connected',
      duration_seconds: 0
    }));
    setCallDialogOpen(true);
  };

  // Open email dialog
  const openEmailDialog = () => {
    setEmailForm({
      to: lead?.work_email || lead?.personal_email || '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      template_id: '',
      attachments: []
    });
    setEmailDialogOpen(true);
  };

  // Open SMS dialog
  const openSmsDialog = () => {
    setSmsForm({
      to: lead?.mobile_phone || lead?.work_direct_phone || '',
      message: '',
      template_id: ''
    });
    setSmsDialogOpen(true);
  };

  return {
    // Email
    emailDialogOpen,
    setEmailDialogOpen,
    emailForm,
    setEmailForm,
    emailBodyRef,
    emailSubjectRef,
    handleEmailTemplateChange,
    handleSendEmail,
    openEmailDialog,
    
    // SMS
    smsDialogOpen,
    setSmsDialogOpen,
    smsForm,
    setSmsForm,
    smsMessageRef,
    handleSmsTemplateChange,
    handleSendSMS,
    openSmsDialog,
    
    // Call
    callDialogOpen,
    setCallDialogOpen,
    callForm,
    setCallForm,
    handleLogCall,
    handleSendQuickEmail,
    openLogCallDialog,
    resetCallForm,
    
    // Attachments
    attachLibraryOpen,
    setAttachLibraryOpen,
    fileInputRef,
    handleFileAttachment,
    removeAttachment,
    addLibraryAttachments,
    
    // Shared
    saving,
    insertVariable,
    handleDeleteCommunication,
    initForms,
  };
}

export default useCrmCommunications;
